import {
  useRef,
  memo,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Icon, Flex, Box, Spinner } from '@chakra-ui/react';
import { FormBuilder } from '@/components/formBuilder';
import { components } from 'react-select';
import { LayoutLeft } from '@kula-ai/icons/dual-tone/LayoutLeft';
import { templatesCategoryConfig } from '@/constants/templates-category';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { useShowToast } from '@/hooks';
import { convertVariablesToConsume } from '@/utils/emailCandidates.utils';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';

export interface EmailTemplate {
  id: number;
  name: string | null;
  channel: string;
  category: string;
  kind: string;
  deleted: boolean;
  subject: string | null;
  body: string | null;
  attachments?: Array<any>;
}

export interface SenderData {
  from?: number;
  cc?: string;
  bcc?: string;
  ats_shared_email?: any;
}

export const EmailCandidates = memo(
  forwardRef(
    (
      {
        emailCandidateData,
        senderData,
        containerHeight,
        previewPayload: payload,
        isCurrentIndex,
        previewDetails = {},
      }: {
        emailCandidateData: EmailTemplate | undefined;
        senderData: SenderData | undefined;
        containerHeight: number;
        previewPayload: any;
        isCurrentIndex: boolean;
        previewDetails?: any;
      },
      ref
    ) => {
      const emailRef = useRef(null);
      const [firstTimeCurrentIndex, setFirstTimeCurrentIndex] = useState(false);
      const currentUser = useSelector(selectCurrUser);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [isLoading, setIsLoading] = useState(false);
      const [templates, setTemplates] = useState(null);
      const [selectedTemplate, setSelectedTemplate] = useState<
        EmailTemplate | Record<string, never>
      >({});
      const hasUserSelectedTemplate = useRef(false);
      const [previewPayload, setPreviewPayload] = useState(payload);
      const interviewerView =
        templatesCategoryConfig.candidate_schedule_notice.view;
      const {
        data: { variables: interviewerVariables = [] } = {},
        isLoading: isLoadingVariables,
      } = useGetTemplatesVariablesQuery({
        params: { view: interviewerView },
      });
      const toast = useShowToast();
      const [templateIsPreview, setTemplateIsPreview] = useState(false);
      const { processFromUserPayload, feSharedEmails, fromUsers } =
        useGetAllUsers();
      const getEmailHasError = () => {
        const { subject, body, from } = emailRef?.current?.values || {};
        const errors = [];

        if (!from) {
          errors.push('Please add a sender');
        }
        if (!subject) {
          errors.push('Please add subject');
        }
        if (!body) {
          errors.push('Please add email body');
        } else if (!body.includes('{{interview.self_schedule_link}}')) {
          errors.push('Schedule link is missing.');
        }
        if (errors.length > 0) {
          return {
            hasError: true,
            message: errors.join(', '),
          };
        }
        return {
          hasError: false,
          message: '',
        };
      };

      const hasEmailDataChanged = () => {
        if (!emailCandidateData) return false;

        return (
          emailRef?.current?.values?.subject !== emailCandidateData.subject ||
          emailRef?.current?.values?.body !== emailCandidateData.body ||
          JSON.stringify(emailRef?.current?.values?.attachments) !==
            JSON.stringify(emailCandidateData.attachments || [])
        );
      };

      const getAttachmentIdOrBlob = (attachments: Array<any>) => {
        return attachments?.map((attachment) => {
          if (attachment.id) {
            return attachment.id;
          }
          return attachment;
        });
      };

      const getModel = () => {
        const containerElement = document.querySelector(
          '.schedule-email-container'
        );
        const hasErrorVariable = containerElement?.querySelector(
          '.empty-variable-alert-container'
        );

        if (hasErrorVariable) {
          toast({
            title: 'Empty variables detected',
            description:
              'The variables marked in red do not have any data. Remove them to continue',
            status: 'error',
          });
          document
            .querySelector('.unsupported')
            .scrollIntoView({ block: 'start' });
        }

        if (
          isCurrentIndex &&
          getEmailHasError()?.hasError &&
          !hasErrorVariable
        ) {
          toast({
            title: getEmailHasError()?.message,
            status: 'error',
          });
        }

        const {
          subject,
          attachments,
          body,
          candidate_template,
          cc,
          bcc,
          from,
          fromUserObject,
        } = emailRef?.current?.values || {};

        let fromPayload = {
          candidate_mail_from: undefined,
        } as any;

        // todo: clean up once shared emails is tested
        if (feSharedEmails) {
          fromPayload = processFromUserPayload({
            selectedUser: fromUserObject || currentUser,
            sharedEmailPayloadKey: 'candidate_mail_ats_shared_email_id',
            payloadKey: 'candidate_mail_from',
          });
        } else {
          fromPayload = {
            candidate_mail_from: from,
          };
        }

        const payload = {
          candidate_mail_subject: subject || '',
          candidate_mail_attachments: getAttachmentIdOrBlob(attachments),
          candidate_mail_body: body || '',
          candidate_mail_template_id: candidate_template || '',
          candidate_mail_updated: hasEmailDataChanged(),
          candidate_mail_cc: cc,
          candidate_mail_bcc: bcc,
          ...fromPayload,
        };

        return {
          hasValidData: !getEmailHasError()?.hasError && !hasErrorVariable,
          payload,
        };
      };

      useImperativeHandle(ref, () => ({
        getModel,
      }));

      useEffect(() => {
        const fetchData = async () => {
          try {
            setIsLoading(true);
            const list = await fetchSelectOptions({
              endPoint:
                'ats_comm_templates?filter_by[channel]=email&filter_by[category][]=candidate_schedule_notice&items=200', //TODO change category
            });
            await setTemplates(list);
          } finally {
            setIsLoading(false);
          }
        };
        fetchData();
      }, []);

      const handleSenderIdChange = useCallback((senderId) => {
        setPreviewPayload({
          ...payload,
          sender_id: senderId || payload?.sender_id || null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // Update form values
      useEffect(() => {
        if (hasUserSelectedTemplate.current) return;

        if (emailCandidateData) {
          const { id, subject, body, attachments } = emailCandidateData;
          const { from, cc, bcc } = senderData || {};
          emailRef?.current?.setValues({
            candidate_template: id,
            subject,
            body,
            attachments,
            from,
            cc,
            bcc,
            fromUserObject: senderData?.ats_shared_email,
          });
          setSelectedTemplate(emailCandidateData);
          handleSenderIdChange(senderData?.from);
        } else if (templates?.length > 0) {
          const firstTemplate = templates[0];
          setSelectedTemplate({
            label: firstTemplate.name,
            value: firstTemplate.id,
            ...firstTemplate,
          });
        }
      }, [emailCandidateData, handleSenderIdChange, senderData, templates]);

      //check for template change and update the editor
      useEffect(() => {
        const { from, cc, bcc } = emailRef?.current?.values || {};
        emailRef?.current?.setValues({
          candidate_template: selectedTemplate?.id,
          subject: selectedTemplate?.subject,
          body: selectedTemplate?.body,
          attachments: selectedTemplate?.attachments || [],
          from,
          cc,
          bcc,
        });
      }, [selectedTemplate]);

      const editorContainerHeight = useMemo((): number => {
        const headerHeight = 200;
        return containerHeight - headerHeight;
      }, [containerHeight]);

      useEffect(() => {
        if (isCurrentIndex && !firstTimeCurrentIndex) {
          setFirstTimeCurrentIndex(true);
        }
      }, [isCurrentIndex, firstTimeCurrentIndex]);

      const emailFieldConfigs = useEmailField({
        formRef: emailRef,
        showFromField: true,
        bodyLabel: '',
        templateData: firstTimeCurrentIndex && selectedTemplate,
        variables: convertVariablesToConsume(interviewerVariables),
        replaceContent: true,
        getEditorInstance: true,
        enableFontSize: true,
        enableFontFamily: true,
        containerPropStyles: {
          h: editorContainerHeight + 'px',
          overflow: 'auto',
        },
        valueKey: 'id',
        ccBccValueKey: 'email',
        editorHeight: editorContainerHeight,
        disableFrom: templateIsPreview,
        templateIsPreview: templateIsPreview,
        previewProps: {
          payload: previewPayload,
          enable: true,
          onClick: (action) => {
            setTemplateIsPreview(action);
          },
          previewDetails,
        },
        toolBarProps: {
          top: {
            position: 'sticky',
            top: 0,
          },
        },
      });

      const handleTemplateChange = (value) => {
        hasUserSelectedTemplate.current = true;
        setSelectedTemplate(value);
      };

      const CustomValueContainer = ({ children, ...props }) => (
        <components.ValueContainer {...props}>
          <Flex alignItems="center" gap="4px">
            <Icon as={LayoutLeft} color="secondary-700" />
            <Box>{children}</Box>
          </Flex>
        </components.ValueContainer>
      );

      const formConfig = [
        {
          name: 'candidate_template',
          type: 'kSelect',
          options: templates?.map?.((template) => ({
            label: template?.name,
            value: template?.id,
            ...template,
          })),
          customOnChange: handleTemplateChange,
          placeholder: 'Select a template',
          components: {
            IndicatorSeparator: null,
            ValueContainer: CustomValueContainer,
            PlaceholderContainer: ({ children, ...props }) => (
              <components.Placeholder
                {...props}
                style={{ paddingLeft: '10px' }}
              >
                {children}
              </components.Placeholder>
            ),
          },
          isRequired: true,
          isDisabled: templateIsPreview,
        },
        {
          id: 'emailFormConfig',
          type: 'wrapper',
          fields: emailFieldConfigs,
          Wrapper: Box,
          wrapperProps: {
            border: '1px solid',
            borderColor: 'border-color',
            borderRadius: 'sm',
          },
        },
      ];

      const handleSenderChange = (formValues) => {
        const { fromUserObject } = formValues;
        const fromPayload = processFromUserPayload({
          selectedUser: fromUserObject,
          payloadKey: 'senderId',
        }) as { senderId?: any; ats_shared_email_id?: any };
        const senderId = fromPayload?.senderId || fromUserObject?.id || null;
        const ats_shared_email_id = fromPayload?.ats_shared_email_id;
        setPreviewPayload({
          ...payload,
          sender_id: senderId || payload?.sender_id || null,
          ...(ats_shared_email_id && { ats_shared_email_id }),
        });
      };
      const initialValues = useMemo(() => {
        const currentMailBoxConnectedUser = fromUsers?.find(
          (user) => user?.id === currentUser?.id
        );
        return {
          candidate_template: '',
          subject: '',
          body: '',
          attachments: [],
          from: currentMailBoxConnectedUser?.id ?? undefined,
          fromUserObject: currentMailBoxConnectedUser ?? {},
          cc: '',
          bcc: '',
        };
      }, [currentUser, fromUsers]);
      if (isLoadingVariables) {
        return (
          <Flex justifyContent="center" alignItems="center" h="100%" w="100%">
            <Spinner />
          </Flex>
        );
      }

      return (
        <Flex
          maxH="100%"
          w="100%"
          justifyContent="center"
          data-test-id="section-email-to-candidates"
          className="schedule-email-container"
        >
          <Flex w="50%" h="100%" flexDir="column">
            <FormBuilder
              config={formConfig}
              formRef={emailRef}
              initialValues={initialValues}
              showCustomError={false}
              isObserveEnabled={true}
              observFunc={handleSenderChange}
            />
          </Flex>
        </Flex>
      );
    }
  )
);

export default EmailCandidates;
